/**
 * The two labelled controls every record form is built out of.
 *
 * They lived in `new-contact.tsx`, which is gone — there is one form per
 * record now, not a short one for creating and a long one for editing — and
 * they were never about contacts anyway. A property, a job and a contact all
 * want the same box with the same label in the same place.
 */
import { useId, type ReactNode } from 'react'

/**
 * A label, a slot and a hint — what every field in the product is made of.
 *
 * Exported because three editors had grown their own: an 11px **uppercase**
 * label over a slot, which is the drift `design-remediation.md` §0.4 names.
 * A field label is 11.5px sentence case, muted (`design-system.md` §2);
 * uppercase 10.5px belongs to table headers, nav group headings and dialog
 * block titles, and nowhere else.
 *
 * `Field` and `SelectField` below are this plus a control. A caller with its
 * own control — a `MenuSelect`, a read-only box, a row of chips — uses this
 * directly rather than inventing the label again.
 */
export function FieldShell({ label, hint, required, htmlFor, hintId, className, children }: {
  label: string
  hint?: ReactNode
  required?: boolean
  /** The control's id, when the caller owns it. */
  htmlFor?: string
  hintId?: string
  className?: string
  children: ReactNode
}) {
  return (
    <div className={className ?? 'block min-w-0'}>
      {/*
        * The required marker sits outside the label element, not inside it.
        * A label's text is the control's name, and "First name *" is not what
        * the field is called.
        */}
      <span className="mb-1 flex items-baseline gap-0.5 text-fieldlabel text-text-muted">
        {htmlFor ? <label htmlFor={htmlFor}>{label}</label> : <span>{label}</span>}
        {/*
          * A green dot, not a red asterisk — `design-system.md`, and the
          * build book says it as a rule of the build: "no asterisks". The
          * same 5px dot `components/form/Field` draws, so the two kits do
          * not mark the same thing two ways while they still both exist.
          */}
        {required && (
          <span
            aria-hidden
            className="ml-[3px] inline-block h-[5px] w-[5px] shrink-0 rounded-full bg-accent"
          />
        )}
      </span>
      {children}
      {hint && (
        <span id={hintId} className="mt-1 block text-sub leading-[1.4] text-text-faint">
          {hint}
        </span>
      )}
    </div>
  )
}

/**
 * One labelled input.
 *
 * The label is tied to the input by id rather than by wrapping it, and the
 * hint is `aria-describedby` rather than more text inside the label. Wrapping
 * meant the accessible name of a field was everything in the label element —
 * so a screen reader announced the Company select as "Company one shared list,
 * so two people typing the same company get the same company", and the
 * required marker turned "First name" into "First name *". The visible layout
 * is identical; what changed is what the field is called.
 */
export function Field({
  label, value, onChange, type = 'text', placeholder, autoFocus, required, hint,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  type?: string
  placeholder?: string
  autoFocus?: boolean
  required?: boolean
  /** Said after the name, not as part of it. */
  hint?: string
}) {
  const id = useId()
  const hintId = `${id}-hint`

  return (
    <FieldShell label={label} hint={hint} required={required} htmlFor={id} hintId={hintId}>
      <input
        id={id}
        type={type}
        value={value}
        required={required}
        aria-describedby={hint ? hintId : undefined}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        autoFocus={autoFocus}
        className="h-9 w-full rounded-field border border-line bg-surface px-2 text-cell text-text placeholder:text-text-faint"
      />
    </FieldShell>
  )
}

/**
 * One labelled dropdown, associated the same way `Field` is.
 *
 * Same reason: a hint inside the label becomes part of the control's name.
 * "Zone" is the name; "routing groups a crew's day by zone" is a description,
 * and a screen reader should say them in that order and tell them apart.
 */
export function SelectField({
  label, value, onChange, children, hint, required, after,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  children: ReactNode
  hint?: ReactNode
  required?: boolean
  /** A button that sits beside the control — "Add new". */
  after?: ReactNode
}) {
  const id = useId()
  const hintId = `${id}-hint`

  return (
    <FieldShell label={label} hint={hint} required={required} htmlFor={id} hintId={hintId}>
      <div className={after ? 'flex gap-2' : undefined}>
        <select
          id={id}
          value={value}
          required={required}
          aria-describedby={hint ? hintId : undefined}
          onChange={(event) => onChange(event.currentTarget.value)}
          className={`h-9 rounded-field border border-line bg-surface px-2 text-cell text-text ${
            after ? 'min-w-0 flex-1' : 'w-full'}`}
        >
          {children}
        </select>
        {after}
      </div>
    </FieldShell>
  )
}

/**
 * One labelled text box, associated the same way `Field` is.
 *
 * The forms were wrapping a `<span>` and a `<textarea>` in a `<label>`, which
 * makes the control's accessible name every word inside that label — so "Access
 * notes" plus the sentence under it, read out as one long name. It also means
 * `getByLabel('Access notes')` finds nothing, which is a fair test of whether
 * the field is labelled at all.
 */
export function TextAreaField({
  label, value, onChange, rows = 3, placeholder, hint,
}: {
  label: string
  value: string
  onChange: (value: string) => void
  rows?: number
  placeholder?: string
  hint?: ReactNode
}) {
  const id = useId()
  const hintId = `${id}-hint`

  return (
    <FieldShell label={label} hint={hint} htmlFor={id} hintId={hintId}>
      <textarea
        id={id}
        value={value}
        rows={rows}
        placeholder={placeholder}
        aria-describedby={hint ? hintId : undefined}
        onChange={(event) => onChange(event.currentTarget.value)}
        className="w-full rounded-field border border-line bg-surface p-2 text-cell text-text placeholder:text-text-faint"
      />
    </FieldShell>
  )
}
